/*
  Absenz- und Ferientypen verwalten.

  Beide Listen sind DS-Tabellen: eine Kopfzeile statt Feldbeschriftungen an
  jeder Zeile. Gespeichert wird beim Verlassen eines Feldes; es gibt bewusst
  keinen Speichern-Knopf. Gelöscht wird nur, was
  nirgends verwendet wird; alles andere wandert ins Archiv, weil die Datenbank
  ein DELETE benutzter Typen ablehnt.
*/

import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Box } from "@mui/material";
import { useTranslation } from "react-i18next";
import AppButton from "../../design-system/components/AppButton";
import AppCallout from "../../design-system/components/AppCallout";
import AppCheckbox from "../../design-system/components/AppCheckbox";
import AppChip from "../../design-system/components/AppChip";
import AppConfirmDialog from "../../design-system/components/AppConfirmDialog";
import AppInput from "../../design-system/components/AppInput";
import AppLoadingOverlay from "../../design-system/components/AppLoadingOverlay";
import AppTable from "../../design-system/components/AppTable";
import AppText from "../../design-system/components/AppText";
import { IconAdd, IconArchive, IconDelete } from "../../design-system/icons";
import useToast from "../../components/useToast";
import {
  createAbsenceType,
  createHolidayType,
  deleteAbsenceType,
  deleteHolidayType,
  getTimeOffTypes,
  updateAbsenceType,
  updateHolidayType,
} from "./timeOffTypesApi";
import SettingsColorPicker from "./SettingsColorPicker";

const ABSENCE_KIND = "absence";
const HOLIDAY_KIND = "holiday";
const DEFAULT_ABSENCE_COLOR = "#1C9A9A";
const ABSENCE_COLOR_PALETTE = Object.freeze([
  "#D64545",
  "#2E9B59",
  "#3478D4",
  "#E0B400",
  "#D94F9D",
  "#24272B",
  "#E97B24",
  "#7B61C9",
  "#1C9A9A",
  "#8A6248",
]);

function asText(value) {
  return String(value ?? "").trim();
}

function hoursToField(value) {
  if (value === null || value === undefined || value === "") return "";
  const parsed = Number(value);
  return Number.isFinite(parsed) ? String(parsed) : "";
}

function fieldToHours(value) {
  const raw = asText(value).replace(",", ".");
  if (!raw) return null;
  const parsed = Number(raw);
  if (!Number.isFinite(parsed) || parsed < 0 || parsed > 24) return null;
  return Math.round(parsed * 100) / 100;
}

function usageLabel(count, t) {
  const value = Number(count) || 0;
  return value === 1
    ? t("settings_ui.time_off_usage_one")
    : t("settings_ui.time_off_usage_other", { count: value });
}

function requestErrorMessage(error, fallback) {
  return (
    error?.response?.data?.message ||
    error?.response?.data?.error ||
    fallback
  );
}

function timeToMinutes(value) {
  const match = /^(\d{1,2}):(\d{2})$/.exec(asText(value));
  if (!match) return null;
  const minutes = Number(match[1]) * 60 + Number(match[2]);
  return Number.isFinite(minutes) ? minutes : null;
}

/*
  Stunden aus der Zeitspanne. 00:00–23:59 ergibt 23.98 – das ist gewollt: wer
  einen glatten Tageswert will, trägt ihn bei Stunden/Tag ein.
*/
function hoursFromSpan(startTime, endTime) {
  const start = timeToMinutes(startTime);
  const end = timeToMinutes(endTime);
  if (start === null || end === null || end <= start) return null;
  return Math.round(((end - start) / 60) * 100) / 100;
}

/*
  Ein Ferientyp wird entweder von der Zeitspanne oder vom Stundenwert bestimmt.
  Statt einer zusätzlichen Spalte leiten wir das aus den Daten ab: deckt sich
  der Stundenwert mit der Spanne, war die Zeit der Ausgangspunkt.
*/
function isTimeDriven(row) {
  const span = hoursFromSpan(row?.start_time, row?.end_time);
  if (span === null) return false;
  const hours = Number(row?.hours_per_day);
  return Number.isFinite(hours) && Math.abs(hours - span) < 0.005;
}

/*
  Der schlanke Settings-Endpunkt liefert aktive und archivierte Typen inklusive
  Verwendungszahlen.
*/
function readTypeLists(payload) {
  return {
    absences: Array.isArray(payload?.absence_list) ? payload.absence_list : [],
    absenceArchive: Array.isArray(payload?.absence_archive_list) ? payload.absence_archive_list : [],
    holidays: Array.isArray(payload?.user_holiday_type_list) ? payload.user_holiday_type_list : [],
    holidayArchive: Array.isArray(payload?.user_holiday_type_archive_list)
      ? payload.user_holiday_type_archive_list
      : [],
  };
}

function nextAvailableName(baseName, rows) {
  const usedNames = new Set(
    (rows || []).map((row) => asText(row?.name).toLocaleLowerCase()).filter(Boolean)
  );
  if (!usedNames.has(baseName.toLocaleLowerCase())) return baseName;

  let suffix = 2;
  while (usedNames.has(`${baseName} ${suffix}`.toLocaleLowerCase())) suffix += 1;
  return `${baseName} ${suffix}`;
}

/*
  Feld, das seinen Wert erst beim Verlassen abschickt. Solange man tippt, gilt
  der lokale Stand; kommt von aussen ein neuer Wert, zieht das Feld nach.
*/
function CommitField({
  value,
  disabled,
  muted = false,
  ariaLabel,
  type = "text",
  trailingText,
  hideTrailingTextWhenEmpty = false,
  placeholder,
  min,
  max,
  step,
  onCommit,
}) {
  const [draft, setDraft] = useState(value);
  const committedRef = useRef(value);

  useEffect(() => {
    committedRef.current = value;
    setDraft(value);
  }, [value]);

  return (
    <AppInput
      size="sm"
      type={type}
      value={draft}
      disabled={disabled}
      className={muted ? "spv2-type-input-muted" : ""}
      placeholder={placeholder}
      trailingText={trailingText}
      hideTrailingTextWhenEmpty={hideTrailingTextWhenEmpty}
      min={min}
      max={max}
      step={step}
      aria-label={ariaLabel}
      onChange={(event) => setDraft(event.target.value)}
      onBlur={() => {
        if (draft === committedRef.current) return;
        onCommit(draft, () => setDraft(committedRef.current));
      }}
    />
  );
}

/*
  Kein zusätzliches Schloss-Symbol: was gesperrt ist, ist ausgegraut – das gilt für das
  Namensfeld von "Freiwunsch" ebenso wie für die Zeiten des Standard-Ferientyps.
  Ein zusätzliches Symbol nur an einer der beiden Stellen las sich willkürlich.
*/
function TypeNameCell({ row, locked, busy, onCommit }) {
  const { t } = useTranslation();

  return (
    <span className="spv2-type-name-cell">
      <CommitField
        value={row.name || ""}
        disabled={locked || busy}
        ariaLabel={t("settings_ui.time_off_name_aria", { name: row.name })}
        onCommit={onCommit}
      />
      {row.is_system_default ? (
        <AppChip variant="muted" appearance="semiFilled" size="sm" className="spv2-type-system-mark">
          {t("settings_ui.time_off_standard")}
        </AppChip>
      ) : null}
    </span>
  );
}

function UsageCell({ row }) {
  const { t } = useTranslation();
  const count = Number(row.usage_count) || 0;

  return (
    <AppText
      as="span"
      variant="body-3"
      tone={count ? "primary" : "tertiary"}
      className="spv2-type-usage"
    >
      {count ? usageLabel(count, t) : t("settings_ui.time_off_not_used")}
    </AppText>
  );
}

function RemoveButton({ row, busy, onClick }) {
  const { t } = useTranslation();
  const willArchive = Boolean(row.usage_count);
  const locked = Boolean(row.is_system_default);
  const label = locked
    ? t("settings_ui.time_off_protected_aria", { name: row.name })
    : willArchive
      ? t("settings_ui.time_off_archive_aria", { name: row.name })
      : t("settings_ui.time_off_delete_aria", { name: row.name });

  return (
    <AppButton
      variant="neutral"
      size="sm"
      iconOnly
      disabled={busy || locked}
      aria-label={label}
      onClick={onClick}
    >
      {willArchive ? <IconArchive sx={{ fontSize: 16 }} /> : <IconDelete sx={{ fontSize: 16 }} />}
    </AppButton>
  );
}

function TypeGroup({ title, description, addLabel, busy, children, onAdd }) {
  return (
    <Box className="spv2-type-group">
      <Box className="spv2-type-group-head">
        <Box className="spv2-type-group-copy">
          <AppText as="h3" variant="heading-4" className="spv2-type-group-title">
            {title}
          </AppText>
          <AppText as="p" variant="body-3" tone="secondary" className="spv2-type-group-description">
            {description}
          </AppText>
        </Box>
        <AppButton variant="neutral" size="sm" disabled={busy} onClick={onAdd}>
          <IconAdd />
          {addLabel}
        </AppButton>
      </Box>
      {children}
    </Box>
  );
}

function ArchiveList({ rows, busy, onRestore }) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  if (!rows.length) return null;

  return (
    <Box className="spv2-type-archive">
      <AppButton
        variant="muted"
        size="sm"
        aria-expanded={open}
        onClick={() => setOpen((current) => !current)}
      >
        <IconArchive />
        {open
          ? t("settings_ui.time_off_archive_hide")
          : t("settings_ui.time_off_archive_count", { count: rows.length })}
      </AppButton>

      {open ? (
        <Box className="spv2-type-archive-list">
          {rows.map((row) => (
            <Box key={row.id} className="spv2-type-archive-row">
              <AppText as="span" variant="body-2" truncate className="spv2-type-archive-name">
                {row.name}
              </AppText>
              <AppText as="span" variant="body-3" tone="tertiary">
                {row.usage_count
                  ? usageLabel(row.usage_count, t)
                  : t("settings_ui.time_off_not_used")}
              </AppText>
              <AppButton variant="neutral" size="sm" disabled={busy} onClick={() => onRestore(row)}>
                {t("settings_ui.time_off_restore")}
              </AppButton>
            </Box>
          ))}
        </Box>
      ) : null}
    </Box>
  );
}

export function TimeOffTypesSection({ active, token }) {
  const { t } = useTranslation();
  const { success } = useToast();
  const [lists, setLists] = useState({
    absences: [],
    absenceArchive: [],
    holidays: [],
    holidayArchive: [],
  });
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [pendingDelete, setPendingDelete] = useState(null);
  const loadedRef = useRef(false);

  const applyPayload = useCallback((payload) => {
    setLists(readTypeLists(payload));
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      applyPayload(await getTimeOffTypes(token));
      loadedRef.current = true;
    } catch (requestError) {
      setError(requestErrorMessage(requestError, t("settings_ui.time_off_load_error")));
    } finally {
      setLoading(false);
    }
  }, [applyPayload, t, token]);

  useEffect(() => {
    if (!active || loadedRef.current) return;
    load();
  }, [active, load]);

  const run = useCallback(
    async (request, successMessage) => {
      setBusy(true);
      setError("");
      try {
        applyPayload(await request());
        if (successMessage) success(successMessage);
        return true;
      } catch (requestError) {
        const message = requestErrorMessage(requestError, t("settings_ui.time_off_save_error"));
        // Der Server bleibt die Wahrheit: nach einem Fehler neu laden, damit
        // die Zeile nicht einen Wert zeigt, der nie gespeichert wurde. Die
        // Meldung kommt erst danach, weil das Laden den Fehler zurücksetzt.
        await load();
        setError(message);
        return false;
      } finally {
        setBusy(false);
      }
    },
    [applyPayload, load, success, t]
  );

  const changeAbsence = useCallback(
    (row, patch, successMessage) => run(
      () => updateAbsenceType(token, row.id, patch),
      successMessage || t("settings_ui.time_off_absence_saved", { name: patch.name || row.name })
    ),
    [run, t, token]
  );
  const changeHoliday = useCallback(
    (row, patch, successMessage) => run(
      () => updateHolidayType(token, row.id, patch),
      successMessage || t("settings_ui.time_off_holiday_saved", { name: patch.name || row.name })
    ),
    [run, t, token]
  );

  /*
    Zeitspanne ändern heisst zugleich: die Stunden ergeben sich daraus. Der
    berechnete Wert geht mit, damit Anzeige und gespeicherter Wert nicht
    auseinanderlaufen — er ist ja das, was beim Erfassen gebucht wird.
  */
  const commitHolidaySpan = useCallback(
    (row, patch) => {
      const next = {
        start_time: row.start_time || "00:00",
        end_time: row.end_time || "23:59",
        ...patch,
      };
      const hours = hoursFromSpan(next.start_time, next.end_time);
      return changeHoliday(row, hours === null ? next : { ...next, hours_per_day: hours });
    },
    [changeHoliday]
  );

  const addAbsence = useCallback(() => {
    const name = nextAvailableName(
      t("settings_ui.time_off_new_absence_type"),
      [...lists.absences, ...lists.absenceArchive]
    );
    return run(
      () => createAbsenceType(token, { name, color_hex: DEFAULT_ABSENCE_COLOR }),
      t("settings_ui.time_off_absence_created", { name })
    );
  }, [lists.absenceArchive, lists.absences, run, t, token]);
  const addHoliday = useCallback(() => {
    const name = nextAvailableName(
      t("settings_ui.time_off_new_holiday_type"),
      [...lists.holidays, ...lists.holidayArchive]
    );
    return run(
      () => createHolidayType(token, { name, hours_per_day: 8.4 }),
      t("settings_ui.time_off_holiday_created", { name })
    );
  }, [lists.holidayArchive, lists.holidays, run, t, token]);

  const confirmDelete = useCallback(async () => {
    if (!pendingDelete) return;
    const { kind, row } = pendingDelete;
    setPendingDelete(null);
    const outcome = row.usage_count ? "archived" : "deleted";
    await run(
      () => (kind === ABSENCE_KIND
        ? deleteAbsenceType(token, row.id)
        : deleteHolidayType(token, row.id)),
      t(`settings_ui.time_off_${kind}_${outcome}`, { name: row.name })
    );
  }, [pendingDelete, run, t, token]);

  const absenceColumns = useMemo(
    () => [
      {
        key: "color",
        label: "",
        width: 76,
        align: "center",
        sortable: false,
        render: (_value, row) => (
          <SettingsColorPicker
            value={row.color_hex || DEFAULT_ABSENCE_COLOR}
            label={row.name}
            disabled={busy}
            palette={ABSENCE_COLOR_PALETTE}
            onChange={(color) => changeAbsence(row, { color_hex: color })}
          />
        ),
      },
      {
        key: "name",
        label: t("settings_ui.name"),
        minWidth: 240,
        render: (_value, row) => (
          <TypeNameCell
            row={row}
            locked={Boolean(row.is_system_default)}
            busy={busy}
            onCommit={(next, revert) => {
              if (!asText(next)) return revert();
              return changeAbsence(row, { name: asText(next) });
            }}
          />
        ),
      },
      {
        key: "hours_per_day",
        label: t("settings_ui.time_off_hours_per_day"),
        width: 124,
        sortable: false,
        render: (_value, row) => (
          <CommitField
            type="number"
            value={hoursToField(row.hours_per_day)}
            disabled={busy || row.counts_as_work_time === false}
            placeholder={t("settings_ui.time_off_daily_target")}
            trailingText="h"
            hideTrailingTextWhenEmpty
            min="0"
            max="24"
            step="0.01"
            ariaLabel={t("settings_ui.time_off_hours_aria", { name: row.name })}
            onCommit={(next, revert) => {
              if (!asText(next)) return changeAbsence(row, { hours_per_day: null });
              const hours = fieldToHours(next);
              if (hours === null) return revert();
              return changeAbsence(row, { hours_per_day: hours });
            }}
          />
        ),
      },
      {
        key: "counts_as_work_time",
        label: t("settings_ui.time_off_work_time"),
        width: 96,
        align: "center",
        sortable: false,
        render: (_value, row) => (
          <AppCheckbox
            checked={row.counts_as_work_time !== false}
            disabled={busy}
            aria-label={t("settings_ui.time_off_counts_as_work_aria", { name: row.name })}
            onChange={(event) => changeAbsence(row, { counts_as_work_time: event.target.checked })}
          />
        ),
      },
      {
        key: "usage_count",
        label: t("settings_ui.time_off_usage"),
        // Feste Breite: die freie Breite soll dem Namen zugutekommen.
        width: 140,
        sortable: false,
        render: (_value, row) => <UsageCell row={row} />,
      },
      {
        key: "actions",
        label: "",
        width: 60,
        align: "center",
        sortable: false,
        render: (_value, row) => (
          <RemoveButton
            row={row}
            busy={busy}
            onClick={() => setPendingDelete({ kind: ABSENCE_KIND, row })}
          />
        ),
      },
    ],
    [busy, changeAbsence, t]
  );

  const holidayColumns = useMemo(
    () => [
      {
        key: "name",
        label: t("settings_ui.name"),
        minWidth: 240,
        render: (_value, row) => (
          <TypeNameCell
            row={row}
            // Der Standard-Ferientyp wird über is_system_default gefunden,
            // Umbenennen ist darum gefahrlos.
            locked={false}
            busy={busy}
            onCommit={(next, revert) => {
              if (!asText(next)) return revert();
              return changeHoliday(row, { name: asText(next) });
            }}
          />
        ),
      },
      {
        key: "hours_per_day",
        label: t("settings_ui.time_off_hours_per_day"),
        width: 124,
        sortable: false,
        render: (_value, row) => (
          <CommitField
            type="number"
            value={hoursToField(row.hours_per_day)}
            disabled={busy}
            // Kommt der Wert aus der Zeitspanne, ist er nur abgeleitet.
            muted={isTimeDriven(row)}
            trailingText="h"
            min="0"
            max="24"
            step="0.01"
            ariaLabel={t("settings_ui.time_off_hours_aria", { name: row.name })}
            onCommit={(next, revert) => {
              const hours = fieldToHours(next);
              if (hours === null) return revert();
              return changeHoliday(row, { hours_per_day: hours });
            }}
          />
        ),
      },
      {
        key: "start_time",
        label: t("settings_ui.from"),
        width: 132,
        sortable: false,
        render: (_value, row) => (
          <CommitField
            type="time"
            value={row.start_time || "00:00"}
            disabled={busy || Boolean(row.is_system_default)}
            muted={!isTimeDriven(row)}
            ariaLabel={t("settings_ui.time_off_start_aria", { name: row.name })}
            onCommit={(next) => commitHolidaySpan(row, { start_time: asText(next) || "00:00" })}
          />
        ),
      },
      {
        key: "end_time",
        label: t("settings_ui.until"),
        width: 132,
        sortable: false,
        render: (_value, row) => (
          <CommitField
            type="time"
            value={row.end_time || "23:59"}
            disabled={busy || Boolean(row.is_system_default)}
            muted={!isTimeDriven(row)}
            ariaLabel={t("settings_ui.time_off_end_aria", { name: row.name })}
            onCommit={(next) => commitHolidaySpan(row, { end_time: asText(next) || "23:59" })}
          />
        ),
      },
      {
        key: "usage_count",
        label: t("settings_ui.time_off_usage"),
        // Feste Breite: die freie Breite soll dem Namen zugutekommen.
        width: 140,
        sortable: false,
        render: (_value, row) => <UsageCell row={row} />,
      },
      {
        key: "actions",
        label: "",
        width: 60,
        align: "center",
        sortable: false,
        render: (_value, row) => (
          <RemoveButton
            row={row}
            busy={busy}
            onClick={() => setPendingDelete({ kind: HOLIDAY_KIND, row })}
          />
        ),
      },
    ],
    [busy, changeHoliday, commitHolidaySpan, t]
  );

  const markSystemRow = useCallback(
    (row) => (row.is_system_default ? "spv2-type-row--system" : ""),
    []
  );

  const willArchive = Boolean(pendingDelete?.row?.usage_count);
  const isInitialLoading = loading && !loadedRef.current;

  return (
    <Box className="spv2-type-settings">
      {/* Ladeanzeige des Design Systems, im Bereich statt über der ganzen Seite. */}
      <AppLoadingOverlay
        open={isInitialLoading}
        scope="contained"
        blocking={false}
        label={t("settings_ui.time_off_loading")}
      />
      {error ? (
        <div role="alert" aria-live="assertive">
          <AppCallout variant="error" description={error} />
        </div>
      ) : null}

      <TypeGroup
        title={t("settings_ui.time_off_absence_types")}
        description={t("settings_ui.time_off_absence_description")}
        addLabel={t("settings_ui.time_off_add_absence_type")}
        busy={busy}
        onAdd={addAbsence}
      >
        <AppTable
          className="spv2-type-table"
          density="compact"
          ariaLabel={t("settings_ui.time_off_absence_types")}
          columns={absenceColumns}
          rows={lists.absences}
          getRowClassName={markSystemRow}
          showFooter={false}
          emptyState={t("settings_ui.time_off_no_absence_types")}
        />
        <ArchiveList
          rows={lists.absenceArchive}
          busy={busy}
          onRestore={(row) => changeAbsence(
            row,
            { is_active: true },
            t("settings_ui.time_off_absence_restored", { name: row.name })
          )}
        />
      </TypeGroup>

      <TypeGroup
        title={t("settings_ui.time_off_holiday_types")}
        description={t("settings_ui.time_off_holiday_description")}
        addLabel={t("settings_ui.time_off_add_holiday_type")}
        busy={busy}
        onAdd={addHoliday}
      >
        <AppTable
          className="spv2-type-table"
          density="compact"
          ariaLabel={t("settings_ui.time_off_holiday_types")}
          columns={holidayColumns}
          rows={lists.holidays}
          getRowClassName={markSystemRow}
          showFooter={false}
          emptyState={t("settings_ui.time_off_no_holiday_types")}
        />
        <ArchiveList
          rows={lists.holidayArchive}
          busy={busy}
          onRestore={(row) => changeHoliday(
            row,
            { is_active: true },
            t("settings_ui.time_off_holiday_restored", { name: row.name })
          )}
        />
      </TypeGroup>

      <AppConfirmDialog
        open={Boolean(pendingDelete)}
        title={willArchive
          ? t("settings_ui.time_off_archive_title", { name: pendingDelete?.row?.name })
          : t("settings_ui.time_off_delete_title", { name: pendingDelete?.row?.name })}
        description={
          willArchive
            ? t("settings_ui.time_off_archive_description", {
                usage: usageLabel(pendingDelete?.row?.usage_count, t),
              })
            : t("settings_ui.time_off_delete_description")
        }
        confirmLabel={willArchive
          ? t("settings_ui.time_off_archive")
          : t("settings_ui.time_off_delete")}
        cancelLabel={t("settings_ui.time_off_cancel")}
        confirmTone={willArchive ? "default" : "error"}
        icon={willArchive ? <IconArchive /> : <IconDelete />}
        onConfirm={confirmDelete}
        onClose={() => setPendingDelete(null)}
      />
    </Box>
  );
}

export default TimeOffTypesSection;
